import { Alert, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { CREDIT_PRICE_TYPES, type AddCreditForm } from '../add-credit-form/add-credit-form-schema';
import { ScheduleRadioCards } from './ScheduleRadioCards';
import { CreditKindRadioCards } from './CreditKindRadioCards';
import { CreditTypeAutocomplete } from './CreditTypeAutocomplete';
import { PriceTypeRadioCards } from './PriceTypeRadioCards';
import { ExpirationPolicySelect } from './ExpirationPolicySelect';
import { VyDateField } from '../../../../../../components/design/inputs';
import { CREDIT_EXPIRATION_POLICIES, CREDIT_KINDS, CREDIT_SCHEDULES } from '@vy/types';

export const AddCreditDrawerContent: React.FC = () => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AddCreditForm>();

  const priceType = watch('priceType');
  const schedule = watch('schedule');
  const isOneTime = priceType === CREDIT_PRICE_TYPES.OneTime;

  useEffect(() => {
    if (isOneTime) {
      setValue('schedule', CREDIT_SCHEDULES.ONE_TIME, { shouldValidate: true });
      setValue('expirationPolicy', CREDIT_EXPIRATION_POLICIES.NEVER);
      return;
    }

    setValue('expiryDate', undefined);
    setValue('expirationPolicy', CREDIT_EXPIRATION_POLICIES.AT_NEXT_GRANT);

    if (schedule === CREDIT_SCHEDULES.ONE_TIME) {
      setValue('schedule', CREDIT_SCHEDULES.RECURRING_MONTHLY, { shouldValidate: true });
    }
  }, [isOneTime]);

  return (
    <Stack spacing={4}>
      <CreditTypeAutocomplete />

      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          Credit type
        </Typography>
        <CreditKindRadioCards />
      </Stack>

      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          Price type
        </Typography>
        <PriceTypeRadioCards />
      </Stack>

      {!isOneTime && (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Cadence
          </Typography>
          <ScheduleRadioCards />
        </Stack>
      )}

      <TextField
        label="Amount"
        type="number"
        fullWidth
        data-vy="add-credit-drawer-amount-input"
        slotProps={{
          input:
            watch('creditKind') === CREDIT_KINDS.MONETARY
              ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
              : { endAdornment: <InputAdornment position="end">units</InputAdornment> },
          inputLabel: { sx: { fontSize: '14px !important' } },
        }}
        {...register('amount', { valueAsNumber: true })}
        error={errors.amount !== undefined}
        helperText={errors.amount?.message}
      />

      <Alert severity="info" data-vy="add-credit-drawer-availability-notice" sx={{ borderRadius: '8px' }}>
        <Typography variant="body2">Credits become available once the invoice is approved.</Typography>
      </Alert>

      {!isOneTime && <ExpirationPolicySelect />}

      {isOneTime && (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Expiry date (Optional)
          </Typography>
          <VyDateField name="expiryDate" />
        </Stack>
      )}
    </Stack>
  );
};
